/**
 * Custom Bundle Picker — "Build your box" widget for Biscuits-backed bundles.
 *
 * Renders a friendly preset + stepper picker for a fixed-size bundle, then adds a
 * single bundle line item to the cart carrying the exact line-item properties that
 * Biscuits Bundles' backend expects (reverse-engineered — see DEBUGGING.md):
 *
 *   id:         <bundle variant id>, quantity: 1
 *   properties: {
 *     _bundleId:      "<RAND4>-<bundleVariantId>",
 *     _selectedItems: JSON [{ i:"<componentVariantId>", q:<qty>, s:<step> }, ...] (q > 0 only),
 *     "Products <step>": "8x Title, 7x Title, ..."
 *   }
 *
 * Config is read from data-* attributes and two embedded JSON <script> tags
 * (.cbp-flavours and .cbp-presets). The widget is self-contained: it owns its own
 * add-to-cart and does not depend on the Biscuits storefront form being visible.
 */
class CustomBundlePicker extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    this.bundleVariantId = this.getAttribute('data-bundle-variant-id');
    this.packSize = parseInt(this.getAttribute('data-pack-size'), 10) || 0;
    // For a fixed-size bundle min === max === packSize. min can be lower for flexible bundles.
    this.minSize = parseInt(this.getAttribute('data-min-size'), 10) || this.packSize;
    this.step = parseInt(this.getAttribute('data-step-number'), 10) || 1;
    this.bundlePrice = parseInt(this.getAttribute('data-bundle-price'), 10) || 0;
    this.comparePrice = parseInt(this.getAttribute('data-compare-price'), 10) || 0;
    this.onSuccess = this.getAttribute('data-on-success') || 'cart'; // 'cart' | 'drawer'
    this.flavourSource = this.getAttribute('data-flavour-source') || 'liquid'; // 'liquid' | 'biscuits'
    this.sizeSuffix = (this.getAttribute('data-size-suffix') || '').trim(); // e.g. "85g" — overrides real weight in titles
    this.defaultPreset = (this.getAttribute('data-default-preset') || '').trim(); // preset name to pre-apply on load

    this.presets = this.readJSON('.cbp-presets') || [];
    this.flavours = (this.readJSON('.cbp-flavours') || []).filter((f) => f.available !== false && f.id);
    this.submitting = false;

    if (!this.bundleVariantId || !this.packSize) {
      console.warn('[CustomBundlePicker] Missing bundle variant or pack size; leaving fallback markup.', {
        bundleVariantId: this.bundleVariantId,
        packSize: this.packSize,
      });
      return;
    }

    if (this.flavours.length) {
      this.start();
    } else if (this.flavourSource === 'biscuits') {
      // No Liquid-provided flavours: hydrate the component list from the (hidden) Biscuits form.
      this.renderLoading();
      this.hydrateFromBiscuits()
        .then((flavours) => {
          this.flavours = flavours.filter((f) => f.available !== false && f.id);
          if (this.flavours.length) this.start();
          else this.showFatal('Bundle flavours are loading — please refresh if this persists.');
        })
        .catch((err) => {
          console.error('[CustomBundlePicker] Biscuits hydration failed', err);
          this.showFatal('Bundle flavours are loading — please refresh if this persists.');
        });
    } else {
      console.warn('[CustomBundlePicker] No flavours configured.');
    }
  }

  start() {
    // Assign a stable colour per flavour for the composition bar + row dots.
    this.flavours.forEach((f, i) => { f.color = CustomBundlePicker.PALETTE[i % CustomBundlePicker.PALETTE.length]; });
    // Curated presets: fill in any missing subtitles. Otherwise offer a restrained auto set.
    if (this.presets.length) {
      this.presets.forEach((p) => { if (p.counts && !p.subtitle) p.subtitle = this.presetSubtitle(p.counts); });
    } else {
      this.presets = this.buildDefaultPresets();
    }
    // Selection state: variantId(string) -> qty
    this.quantities = new Map(this.flavours.map((f) => [String(f.id), 0]));
    this.render();
    this.bindEvents();
    this.applyDefaultPreset();
  }

  applyDefaultPreset() {
    if (!this.defaultPreset || !this.presets.length) return;
    const idx = this.presets.findIndex((p) => (p.name || '').toLowerCase() === this.defaultPreset.toLowerCase());
    if (idx >= 0) this.applyPreset(idx);
  }

  /* Restrained auto presets: an even split and a "<top flavour> lovers" weighting. */
  buildDefaultPresets() {
    const avail = this.flavours.filter((f) => f.available !== false);
    if (avail.length < 2) return [];
    const presets = [];

    const even = this.distributeEven(avail);
    presets.push({ name: 'Even mix', subtitle: this.presetSubtitle(even), counts: even });

    const counts = {};
    const half = Math.min(this.packSize, Math.ceil(this.packSize * 0.6));
    counts[String(avail[0].id)] = half;
    this.distributeInto(counts, avail.slice(1), this.packSize - half);
    presets.push({ name: `${avail[0].title} lovers`, subtitle: this.presetSubtitle(counts), counts });

    return presets;
  }

  distributeEven(list) {
    const counts = {};
    const base = Math.floor(this.packSize / list.length);
    let rem = this.packSize - base * list.length;
    list.forEach((f, i) => { counts[String(f.id)] = base + (i < rem ? 1 : 0); });
    return counts;
  }

  distributeInto(counts, list, amount) {
    if (!list.length || amount <= 0) return;
    const base = Math.floor(amount / list.length);
    let rem = amount - base * list.length;
    list.forEach((f, i) => { counts[String(f.id)] = (counts[String(f.id)] || 0) + base + (i < rem ? 1 : 0); });
  }

  presetSubtitle(counts) {
    return this.flavours.map((f) => counts[String(f.id)] || 0).join(' · ');
  }

  /* Mini composition strip shown under a preset chip. */
  presetMini(preset) {
    const counts = preset.counts || {};
    return this.flavours.map((f) => {
      const q = counts[String(f.id)] || 0;
      if (!q) return '';
      return `<span style="width:${(q / this.packSize) * 100}%;background:${f.color}"></span>`;
    }).join('');
  }

  /**
   * Build the flavour list from the Biscuits bundle form on the page. Biscuits items expose
   * data-biscuits-selected-variant-id, data-biscuits-handle and data-biscuits-available; we
   * enrich title/price/image from {handle}.js. Works even for products hidden from collections,
   * and stays in sync with whatever the merchant configures in Biscuits.
   */
  async hydrateFromBiscuits() {
    const form = await this.waitFor(() => document.querySelector('biscuits-bundle-form .biscuits-bundle-item[data-biscuits-selected-variant-id]') && document.querySelector('biscuits-bundle-form'), 8000);
    if (!form) return [];

    const items = Array.from(form.querySelectorAll('.biscuits-bundle-item[data-biscuits-selected-variant-id]'));
    const seen = new Set();
    const rows = [];
    for (const item of items) {
      const variantId = item.getAttribute('data-biscuits-selected-variant-id');
      if (!variantId || seen.has(variantId)) continue;
      seen.add(variantId);
      const handle = item.getAttribute('data-biscuits-handle') || '';
      const available = item.getAttribute('data-biscuits-available') !== 'false';
      const domImg = item.querySelector('img');
      rows.push({ variantId, handle, available, domImgSrc: domImg ? domImg.src : '' });
    }

    // Enrich from {handle}.js (title, price, image). Fall back to DOM data if a fetch fails.
    const enriched = await Promise.all(rows.map(async (r) => {
      let title = r.handle;
      let price = 0;
      let image = r.domImgSrc;
      if (r.handle) {
        try {
          const p = await fetch(`${window.Shopify.routes.root}products/${r.handle}.js`).then((res) => res.json());
          title = p.title || title;
          const variant = (p.variants || []).find((v) => String(v.id) === String(r.variantId)) || p.variants?.[0];
          price = variant ? variant.price : (p.price || 0);
          if (p.featured_image) image = p.featured_image;
        } catch (e) { /* keep fallbacks */ }
      }
      return {
        id: r.variantId,
        title: this.displayTitle(title),
        fullTitle: title,
        descriptor: '',
        image: image ? this.resizeImg(image, 120) : '',
        price,
        available: r.available,
      };
    }));
    return enriched;
  }

  cleanTitle(title) {
    // Strip brand/format words but keep the size (e.g. "85g").
    return String(title)
      .replace(/Dr Bugs /gi, '')
      .replace(/ Microwave/gi, '')
      .replace(/ Popcorn/gi, '')
      .replace(/ Gourmet/gi, '')
      .replace(/ Treats?/gi, '')
      .replace(/ \(Limited Edition\)/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  // Display title: cleaned title, keeping the real weight — unless a size suffix override
  // is configured, in which case the real weight is replaced with it (e.g. force "85g").
  displayTitle(raw) {
    let t = this.cleanTitle(raw);
    if (this.sizeSuffix) {
      t = t.replace(/\s*\d+\s*g\b/gi, '').replace(/\s{2,}/g, ' ').trim();
      t = `${t} ${this.sizeSuffix}`;
    }
    return t;
  }

  resizeImg(url, width) {
    try {
      const clean = url.split('?')[0];
      return `${clean}?width=${width}`;
    } catch (e) { return url; }
  }

  waitFor(fn, timeout) {
    return new Promise((resolve) => {
      const found = fn();
      if (found) return resolve(found);
      const start = Date.now();
      const iv = setInterval(() => {
        const got = fn();
        if (got || Date.now() - start > timeout) {
          clearInterval(iv);
          resolve(got || null);
        }
      }, 150);
    });
  }

  readJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('[CustomBundlePicker] Could not parse', selector, err);
      return null;
    }
  }

  get total() {
    let t = 0;
    this.quantities.forEach((q) => (t += q));
    return t;
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100);
  }

  /* ---------------------------------------------------------------- render */

  renderLoading() {
    this.innerHTML = `
      <div class="cbp cbp--loading" data-cbp-root>
        <div class="cbp__skeleton" aria-hidden="true"></div>
        <div class="cbp__skeleton"></div>
        <div class="cbp__skeleton"></div>
        <p class="cbp__note">Loading your flavours…</p>
      </div>`;
  }

  showFatal(msg) {
    this.innerHTML = `<div class="cbp"><div class="cbp__error" role="alert">${msg}</div></div>`;
  }

  render() {
    this.innerHTML = `
      <div class="cbp" data-cbp-root>
        <div class="cbp__bar" data-cbp-bar>
          <div class="cbp__bar-track" data-cbp-segments></div>
          <span class="cbp__bar-count" data-cbp-counter aria-live="polite"></span>
        </div>

        ${this.presets.length ? `
        <div class="cbp__quickmix">
          <span class="cbp__quickmix-label">Quick mixes</span>
          <div class="cbp__presets" role="group" aria-label="Quick mixes">
            ${this.presets.map((p, i) => `
              <button type="button" class="cbp__preset" data-cbp-preset="${i}" aria-pressed="false">
                <span class="cbp__preset-top">
                  <span class="cbp__preset-name">${p.name}</span>
                  ${p.subtitle ? `<span class="cbp__preset-sub">${p.subtitle}</span>` : ''}
                </span>
                <span class="cbp__preset-mini" aria-hidden="true">${this.presetMini(p)}</span>
              </button>`).join('')}
          </div>
        </div>` : ''}

        <ul class="cbp__flavours" data-cbp-flavours>
          ${this.flavours.map((f) => this.flavourRow(f)).join('')}
        </ul>

        <div class="cbp__foot">
          <button type="button" class="cbp__atc button button--full-width" id="CbpAddToCart" data-cbp-atc disabled>
            <span data-cbp-atc-label>${this.atcLabel()}</span>
            <span class="cbp__spinner" data-cbp-spinner hidden></span>
          </button>
          <p class="cbp__note" data-cbp-note>${this.noteText()}</p>
          <div class="cbp__error" data-cbp-error role="alert" hidden></div>
        </div>
      </div>`;

    this.el = {
      counter: this.querySelector('[data-cbp-counter]'),
      segments: this.querySelector('[data-cbp-segments]'),
      bar: this.querySelector('[data-cbp-bar]'),
      atc: this.querySelector('[data-cbp-atc]'),
      atcLabel: this.querySelector('[data-cbp-atc-label]'),
      spinner: this.querySelector('[data-cbp-spinner]'),
      note: this.querySelector('[data-cbp-note]'),
      error: this.querySelector('[data-cbp-error]'),
    };

    this.updateUI();
  }

  flavourRow(f) {
    const id = String(f.id);
    const ringStyle = f.color ? `border-color:${f.color}` : '';
    const img = f.image
      ? `<img class="cbp__swatch-img" src="${f.image}" alt="" loading="lazy">`
      : `<span class="cbp__swatch-fallback" aria-hidden="true">🍿</span>`;
    return `
      <li class="cbp__flavour" data-cbp-row="${id}">
        <div class="cbp__swatch" style="${ringStyle}">${img}</div>
        <div class="cbp__meta">
          <span class="cbp__name">${f.title}</span>
          ${f.descriptor ? `<span class="cbp__desc">${f.descriptor}</span>` : ''}
        </div>
        <div class="cbp__stepper" role="group" aria-label="${this.escapeAttr(f.title)} quantity">
          <button type="button" class="cbp__step cbp__step--minus" data-cbp-dec="${id}" aria-label="One fewer ${this.escapeAttr(f.title)}">&minus;</button>
          <span class="cbp__val" data-cbp-val="${id}" aria-live="polite">0</span>
          <button type="button" class="cbp__step cbp__step--plus" data-cbp-inc="${id}" aria-label="One more ${this.escapeAttr(f.title)}">+</button>
        </div>
      </li>`;
  }

  escapeAttr(s) {
    return String(s).replace(/"/g, '&quot;');
  }

  /* ---------------------------------------------------------------- events */

  bindEvents() {
    this.addEventListener('click', (e) => {
      const inc = e.target.closest('[data-cbp-inc]');
      const dec = e.target.closest('[data-cbp-dec]');
      const preset = e.target.closest('[data-cbp-preset]');
      const atc = e.target.closest('[data-cbp-atc]');

      if (inc) this.changeQty(inc.getAttribute('data-cbp-inc'), 1);
      else if (dec) this.changeQty(dec.getAttribute('data-cbp-dec'), -1);
      else if (preset) this.applyPreset(parseInt(preset.getAttribute('data-cbp-preset'), 10));
      else if (atc && !atc.disabled) this.addToCart();
    });
  }

  changeQty(id, delta) {
    id = String(id);
    if (!this.quantities.has(id)) return;
    const next = this.quantities.get(id) + delta;
    if (next < 0) return;
    if (delta > 0 && this.total >= this.packSize) return; // never exceed pack size
    this.quantities.set(id, next);
    this.clearActivePreset();
    this.updateUI();
  }

  applyPreset(index) {
    const preset = this.presets[index];
    if (!preset) return;
    // Reset then apply, clamped to available flavours and pack size.
    this.flavours.forEach((f) => this.quantities.set(String(f.id), 0));
    const counts = preset.counts || {};
    Object.keys(counts).forEach((vid) => {
      const key = String(vid);
      if (this.quantities.has(key)) {
        this.quantities.set(key, Math.max(0, parseInt(counts[vid], 10) || 0));
      }
    });
    // If preset overshoots (e.g. a flavour went out of stock), trim from the end.
    let overflow = this.total - this.packSize;
    if (overflow > 0) {
      for (let i = this.flavours.length - 1; i >= 0 && overflow > 0; i--) {
        const key = String(this.flavours[i].id);
        const take = Math.min(this.quantities.get(key), overflow);
        this.quantities.set(key, this.quantities.get(key) - take);
        overflow -= take;
      }
    }
    this.activePreset = index;
    this.updateUI();
  }

  clearActivePreset() {
    this.activePreset = null;
  }

  /* ---------------------------------------------------------------- ui */

  atcLabel() {
    const total = this.total;
    if (total === 0) return `Choose ${this.packSize} packs`;
    if (total < this.minSize) return `Pick ${this.minSize - total} more`;
    if (total < this.packSize) return `Add ${total}‑pack · ${this.formatMoney(this.bundlePrice)}`;
    return `Add to cart · ${this.formatMoney(this.bundlePrice)}`;
  }

  noteText() {
    const total = this.total;
    if (total > this.packSize) return `Remove ${total - this.packSize} to continue`;
    if (total < this.packSize) return `${this.packSize - total} of ${this.packSize} left to choose`;
    const saving = this.comparePrice && this.comparePrice > this.bundlePrice
      ? `You save ${this.formatMoney(this.comparePrice - this.bundlePrice)}`
      : '';
    return `${saving}`;
  }

  updateUI() {
    const total = this.total;

    // Per-flavour values + stepper disabled states
    this.flavours.forEach((f) => {
      const id = String(f.id);
      const valEl = this.querySelector(`[data-cbp-val="${id}"]`);
      if (valEl) valEl.textContent = this.quantities.get(id);
      const dec = this.querySelector(`[data-cbp-dec="${id}"]`);
      const inc = this.querySelector(`[data-cbp-inc="${id}"]`);
      if (dec) dec.disabled = this.quantities.get(id) <= 0;
      if (inc) inc.disabled = total >= this.packSize;
      const row = this.querySelector(`[data-cbp-row="${id}"]`);
      if (row) row.classList.toggle('is-active', this.quantities.get(id) > 0);
    });

    // Composition bar + count overlay
    const done = total === this.packSize;
    this.el.segments.innerHTML = this.flavours.map((f) => {
      const q = this.quantities.get(String(f.id));
      if (!q) return '';
      return `<span class="cbp__bar-seg" style="width:${(q / this.packSize) * 100}%;background:${f.color}"></span>`;
    }).join('');
    this.el.counter.innerHTML = `<strong>${total}</strong> of ${this.packSize}`;
    this.el.bar.classList.toggle('is-done', done);

    // Presets pressed state
    this.querySelectorAll('[data-cbp-preset]').forEach((btn, i) => {
      btn.setAttribute('aria-pressed', String(this.activePreset === i));
    });

    // ATC + note
    this.el.atcLabel.textContent = this.atcLabel();
    this.el.note.textContent = this.noteText();
    const valid = total >= this.minSize && total <= this.packSize;
    this.el.atc.disabled = !valid || this.submitting;
  }

  /* ---------------------------------------------------------------- cart */

  randomToken() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let out = '';
    for (let i = 0; i < 4; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  }

  buildProperties() {
    const selected = [];
    const summary = [];
    // Preserve flavour display order for a stable, readable summary.
    this.flavours.forEach((f) => {
      const id = String(f.id);
      const q = this.quantities.get(id);
      if (q > 0) {
        selected.push({ i: id, q, s: this.step });
        summary.push(`${q}x ${f.fullTitle || f.title}`);
      }
    });
    const props = {
      _bundleId: `${this.randomToken()}-${this.bundleVariantId}`,
      _selectedItems: JSON.stringify(selected),
    };
    props[`Products ${this.step}`] = summary.join(', ');
    return props;
  }

  async addToCart() {
    if (this.submitting) return;
    if (this.total !== this.packSize && this.total < this.minSize) return;

    this.submitting = true;
    this.setLoading(true);
    this.showError(null);

    const drawer = this.onSuccess === 'drawer' ? document.querySelector('cart-drawer') : null;
    const body = {
      items: [{
        id: parseInt(this.bundleVariantId, 10),
        quantity: 1,
        properties: this.buildProperties(),
      }],
    };
    // Ask Shopify to render the drawer sections in the add response so we can open it in place.
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add this bundle to your cart.');
      }
      const data = await res.json();
      this.dispatchEvent(new CustomEvent('cbp:added', { bubbles: true, detail: { data } }));
      await this.handleSuccess(drawer, data);
    } catch (err) {
      console.error('[CustomBundlePicker] add to cart failed', err);
      this.showError(err.message || 'Something went wrong. Please try again.');
      this.submitting = false;
      this.setLoading(false);
      this.updateUI();
    }
  }

  async handleSuccess(drawer, data) {
    // If a theme cart drawer is present and Shopify returned rendered sections, open it in place.
    if (drawer && data && data.sections && typeof drawer.renderContents === 'function') {
      drawer.renderContents(data);
      this.resetSelection();
      this.submitting = false;
      this.setLoading(false);
      return;
    }
    window.location.href = `${window.Shopify.routes.root}cart`;
  }

  resetSelection() {
    this.flavours.forEach((f) => this.quantities.set(String(f.id), 0));
    this.activePreset = null;
    this.updateUI();
  }

  setLoading(on) {
    this.el.atc.classList.toggle('is-loading', on);
    if (this.el.spinner) this.el.spinner.hidden = !on;
    this.el.atc.disabled = on || this.total < this.minSize || this.total > this.packSize;
  }

  showError(msg) {
    if (!this.el.error) return;
    if (!msg) {
      this.el.error.hidden = true;
      this.el.error.textContent = '';
    } else {
      this.el.error.hidden = false;
      this.el.error.textContent = msg;
    }
  }
}

// Warm, on-brand, visually distinct colours for the composition bar + row dots.
CustomBundlePicker.PALETTE = ['#E0A100', '#B5622F', '#8CC5E0', '#6E8B3D', '#9B5DE5', '#C9455E', '#2C7A4B', '#D68C45'];

if (!customElements.get('custom-bundle-picker')) {
  customElements.define('custom-bundle-picker', CustomBundlePicker);
}
